import { PASSWORD_MIN_LENGTH, TOTP_CODE_DIGITS } from '@bartizo/shared';
import { useCallback, useMemo, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import { reauth as reauthRequest } from '../api/auth.js';
import { isApiError } from '../api/client.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { TextField } from '../ui/Field.js';
import { ReauthContext, type ReauthContextValue } from './reauth-context.js';
import { useSession } from './session-context.js';

/**
 * Section 17's re-check, as the dashboard asks for it.
 *
 * A high-risk action calls `ensureReauthenticated()` and waits. If the credential
 * was entered recently enough the answer is immediate; otherwise a dialog opens and
 * the promise settles when the person either passes it or gives up.
 *
 * Asking *before* the action rather than reacting to the API's 403 matters: the
 * alternative is a destructive button that appears to fail, then works on the
 * second press, which teaches people to press things twice.
 *
 * The client-side freshness check is a courtesy. The API checks it again, and its
 * answer is the one that decides anything.
 */

export function ReauthProvider({ children }: { children: ReactNode }): ReactNode {
  const { hasFreshReauth, noteReauthenticated } = useSession();
  const [open, setOpen] = useState(false);
  const pending = useRef<((passed: boolean) => void) | null>(null);

  const settle = useCallback((passed: boolean): void => {
    setOpen(false);
    const resolve = pending.current;
    pending.current = null;
    resolve?.(passed);
  }, []);

  const ensureReauthenticated = useCallback((): Promise<boolean> => {
    if (hasFreshReauth()) return Promise.resolve(true);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      pending.current = resolve;
    });
  }, [hasFreshReauth]);

  const value = useMemo<ReauthContextValue>(
    () => ({ ensureReauthenticated }),
    [ensureReauthenticated],
  );

  return (
    <ReauthContext value={value}>
      {children}
      {open && (
        <ReauthDialog
          onPassed={(at) => {
            noteReauthenticated(at);
            settle(true);
          }}
          onCancelled={() => {
            settle(false);
          }}
        />
      )}
    </ReauthContext>
  );
}

interface ReauthDialogProps {
  onPassed: (reauthenticatedAt: string) => void;
  onCancelled: () => void;
}

export function ReauthDialog({ onPassed, onCancelled }: ReauthDialogProps): ReactNode {
  const { session } = useSession();
  const totpEnabled = session?.user.totpEnabled === true;
  // With an authenticator in force the code is the quicker answer and the one
  // already in the person's hand, so it leads; the password stays one click away.
  const [useCode, setUseCode] = useState(totpEnabled);
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await reauthRequest(useCode ? { totpCode: value } : { password: value });
      // Cleared before anything else: it has done its job and has no reason to sit
      // in a React state tree afterwards.
      setValue('');
      onPassed(result.reauthenticatedAt);
    } catch (caught: unknown) {
      setError(isApiError(caught) ? caught.message : 'Could not confirm it was you. Try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Confirm it is you"
      description="This action changes a monitored server. Confirm before it runs."
      onClose={onCancelled}
    >
      <form onSubmit={(event) => void submit(event)} className="stack">
        {useCode ? (
          <TextField
            label="Authenticator code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={TOTP_CODE_DIGITS}
            autoFocus
            required
            value={value}
            onChange={(event) => {
              setValue(event.target.value.replace(/\D/g, ''));
            }}
          />
        ) : (
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            autoFocus
            required
            minLength={PASSWORD_MIN_LENGTH}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        )}

        {error !== null && <Alert tone="danger">{error}</Alert>}

        {totpEnabled && (
          <Button
            variant="ghost"
            onClick={() => {
              setUseCode((current) => !current);
              setValue('');
              setError(null);
            }}
          >
            {useCode ? 'Use your password instead' : 'Use your authenticator app instead'}
          </Button>
        )}

        <div className="dialog-actions">
          <Button onClick={onCancelled}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} busyLabel="Checking…">
            Confirm
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
